import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { MessageSquare, X, Send, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONSENT_KEY = 'enbet_chat_consent_v1';
const PROACTIVE_SESSION_KEY = 'enbet_tom_proactive_fired_v1';
const PROACTIVE_SCROLL_THRESHOLD = 0.4;
const PROACTIVE_TIME_MS = 20000;

const PROACTIVE_PAGES = {
  '/': {
    de: 'Hey, schön dass du da bist. Ist das Thema KI gerade relevant für dich?',
    en: "Hey, glad you're here. Is AI a relevant topic for you right now?",
  },
  '/ki-ready-check': {
    de: 'Hey, du schaust dir gerade unseren KI Ready Check an. Hast du konkrete Fragen dazu?',
    en: "Hey, you're checking out our KI Ready Check. Any specific questions?",
  },
  '/ki-strategie-workshop': {
    de: 'Hey, du schaust dir Modul 2 an — Strategie plus erster Agent. Soll ich kurz erklären wie der Tag abläuft?',
    en: "Hey, you're looking at Module 2 — strategy plus first agent. Want me to explain how the day works?",
  },
  '/ki-im-stationaeren-handel': {
    de: 'Hey, du schaust dir KI im stationären Handel an. Was nimmt dich im Tagesgeschäft gerade am meisten in Anspruch — Bestand, Personal oder Pricing?',
    en: "Hey, you're reading about AI in brick-and-mortar retail. What eats most of your daily operations right now — inventory, staffing or pricing?",
  },
  '/ki-fuer-filialisten': {
    de: 'Hey, du schaust dir KI für Mehrstandort-Betriebe an. Wie viele Standorte führst du, und was ist die größte Reibung im Filial-Netz?',
    en: "Hey, you're looking at AI for multi-location businesses. How many locations do you run, and what's the biggest friction across the network?",
  },
  '/ki-strategie-retail': {
    de: 'Hey, du schaust dir KI-Strategie im Retail an. Was ist deine größte Baustelle gerade?',
    en: "Hey, you're reading about AI strategy in retail. What's your biggest open issue right now?",
  },
  '/ki-beratung-kassel': {
    de: 'Hey, schön dass du auf uns aufmerksam geworden bist. Suchst du KI-Beratung im Raum Kassel — und in welcher Branche bist du?',
    en: 'Hey, glad you found us. Looking for AI consulting near Kassel — and what industry are you in?',
  },
  '/ki-handel': {
    de: 'Hey, du bist im Handel — wie viele Filialen führst du, und was nimmt dich im Tagesgeschäft gerade am meisten in Anspruch?',
    en: "Hey, you're in retail — how many locations do you run, and what eats most of your daily operations?",
  },
  '/ki-gastronomie': {
    de: 'Hey, du bist Gastronom — was nervt nach dem Service am meisten: Dienstplan, Bestellungen oder Abrechnung?',
    en: "Hey, you run a gastronomy business — what bugs you most after service: rotas, orders or accounting?",
  },
  '/ki-handwerk': {
    de: 'Hey, du bist im Handwerk — was kostet gerade mehr Zeit: Angebote schreiben, Aufmaß-Doku oder Büro?',
    en: "Hey, you're in the trades — what eats more time right now: quoting, on-site documentation or office work?",
  },
};

const TEXTS = {
  de: {
    initial: 'Hey! Ich bin Tom von EnBet Consulting. Wie heißt du?',
    headerTitle: 'Tom — KI-Assistent',
    openAria: 'Chat öffnen',
    consentWelcome: 'Willkommen beim KI-Assistenten von EnBet Consulting.',
    consentText: 'Um den Chat zu nutzen, stimme bitte unserer ',
    consentPrivacyLink: 'Datenschutzerklärung',
    consentSuffix: ' zu. Darin erklären wir, wie deine Daten im Rahmen dieses Gesprächs verarbeitet werden.',
    consentButton: 'AGB zustimmen & Chat starten',
    inputPlaceholder: 'Nachricht eingeben...',
    bookCalendar: 'Termin buchen',
    bookingConfirmedHeader: 'Termin bestätigt',
    bookingConfirmedText: 'Eine Bestätigung wurde per E-Mail gesendet.',
    meetingLink: 'Meeting-Link',
    error: 'Entschuldigung, ein Fehler ist aufgetreten. Bitte versuche es erneut.',
  },
  en: {
    initial: "Hey! I'm Tom from EnBet Consulting. What's your name?",
    headerTitle: 'Tom — AI Assistant',
    openAria: 'Open chat',
    consentWelcome: "Welcome to EnBet Consulting's AI assistant.",
    consentText: 'To use the chat, please accept our ',
    consentPrivacyLink: 'Privacy Policy',
    consentSuffix: '. It explains how your data is processed during this conversation.',
    consentButton: 'Accept & start chat',
    inputPlaceholder: 'Type a message...',
    bookCalendar: 'Book appointment',
    bookingConfirmedHeader: 'Appointment confirmed',
    bookingConfirmedText: 'A confirmation has been sent via email.',
    meetingLink: 'Meeting link',
    error: 'Sorry, an error occurred. Please try again.',
  },
};

export default function ChatWidget() {
  const { lang } = useLanguage();
  const t = TEXTS[lang] || TEXTS.de;
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [accumulatedLeadData, setAccumulatedLeadData] = useState({});
  const [consented, setConsented] = useState(() => !!localStorage.getItem(CONSENT_KEY));
  const [messages, setMessages] = useState([{ role: 'assistant', content: t.initial }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [proactiveTeaser, setProactiveTeaser] = useState(null);
  // attentionState: 'idle' (kein Hinweis) | 'wave' (Bubble + Button-Animation, wartet auf Klick)
  const [attentionState, setAttentionState] = useState('idle');
  const bottomRef = useRef(null);

  // Lead-ID aus Backend-Response — wird benoetigt fuer end-session (Conversion-Tracking).
  // Ref statt nur State, damit der pagehide-Listener immer den aktuellen Wert sieht.
  const leadIdRef = useRef(null);
  const endSessionFiredRef = useRef(false);

  // Tab-Close / Hide → Backend klassifiziert Chat (Haiku) und schreibt pain_tag + outcome.
  // sendBeacon ueberlebt das Page-Unload (im Gegensatz zu fetch). pagehide ist
  // verlaesslicher als beforeunload (mobile Safari feuert beforeunload nicht).
  useEffect(() => {
    const fireEndSession = (reason) => {
      const lid = leadIdRef.current;
      if (!lid || endSessionFiredRef.current) return;
      endSessionFiredRef.current = true;
      try {
        const url = `${API}/chat/end-session`;
        const payload = JSON.stringify({ lead_id: lid, reason });
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        } else {
          // Fallback: fetch mit keepalive (best effort)
          fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
        }
      } catch {}
    };
    const onPageHide = () => fireEndSession('tab_close');
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') fireEndSession('tab_close');
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Proaktiver Trigger — feuert wenn 40% gescrollt UND 20s vergangen sind,
  // einmal pro Session pro Browser. Nur auf konfigurierten Pages.
  useEffect(() => {
    if (open) return;
    if (typeof window === 'undefined') return;
    try { if (sessionStorage.getItem(PROACTIVE_SESSION_KEY)) return; } catch { return; }

    const pageConfig = PROACTIVE_PAGES[location.pathname];
    if (!pageConfig) return;

    let scrollReached = false;
    let timeReached = false;
    let fired = false;

    const fire = () => {
      if (fired || !scrollReached || !timeReached) return;
      fired = true;
      const teaserText = pageConfig[lang] || pageConfig.de;
      setProactiveTeaser(teaserText);
      setMessages(prev => {
        const hasUserMsg = prev.some(m => m.role === 'user');
        if (hasUserMsg) return prev;
        return [{ role: 'assistant', content: teaserText }];
      });
      // Statt setOpen(true) → nur Aufmerksamkeit anziehen, Kunde muss aktiv klicken
      setAttentionState('wave');
      try { sessionStorage.setItem(PROACTIVE_SESSION_KEY, '1'); } catch {}
    };

    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = window.scrollY / docHeight;
      if (pct >= PROACTIVE_SCROLL_THRESHOLD) {
        scrollReached = true;
        fire();
      }
    };

    const timer = setTimeout(() => { timeReached = true; fire(); }, PROACTIVE_TIME_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
    };
  }, [location.pathname, lang, open]);

  // Sync initial greeting when language changes (only if user hasn't started chatting yet)
  useEffect(() => {
    setMessages(prev => {
      if (prev.length === 1 && prev[0].role === 'assistant') {
        return [{ role: 'assistant', content: t.initial }];
      }
      return prev;
    });
  }, [lang, t.initial]);

  useEffect(() => {
    if (open && consented) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, consented]);

  const handleConsent = () => {
    localStorage.setItem(CONSENT_KEY, '1');
    setConsented(true);
  };

  const send = async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : input).trim();
    if (!text || loading) return;
    if (typeof overrideText !== 'string') setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        message: text,
        history: messages.slice(-10),
        lang,
        accumulated_lead_data: accumulatedLeadData,
        page_path: location.pathname,
      });
      // Build single assistant message with optional alternatives attached
      const assistantMsg = { role: 'assistant', content: data.reply };
      if (data.slot_taken && data.alternatives?.length) {
        assistantMsg.alternatives = data.alternatives;
      }
      setMessages((prev) => [...prev, assistantMsg]);
      if (data.show_calendar) setShowCalendar(true);
      if (data.booking_confirmed) setBookingConfirmed(data.booking_confirmed);
      // Lead-State akkumulieren — Backend hat schon gemerged, einfach übernehmen
      if (data.lead_data) setAccumulatedLeadData(data.lead_data);
      // Lead-ID merken (sobald Backend einen Lead angelegt/gefunden hat),
      // damit pagehide-Hook spaeter end-session triggern kann.
      if (data.lead_id) leadIdRef.current = data.lead_id;
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t.error },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Kurz-Hint für die Sprechblase aus dem Teaser ableiten (erste Halb-Sätze + …)
  const teaserShort = (() => {
    if (!proactiveTeaser) return '';
    const cleaned = proactiveTeaser.replace(/^Hey,?\s*/i, '').trim();
    // Erster Satz oder bis 80 Zeichen
    const dot = cleaned.indexOf('. ');
    const firstSentence = dot > 0 ? cleaned.slice(0, dot + 1) : cleaned;
    return firstSentence.length > 80 ? firstSentence.slice(0, 77) + '…' : firstSentence;
  })();
  const handleOpen = () => {
    setOpen(true);
    setAttentionState('idle');
  };
  const dismissWave = (e) => {
    e.stopPropagation();
    setAttentionState('idle');
  };

  return (
    <>
      {/* Subtile Bounce-Animation für den Button im Wave-Mode */}
      {!open && attentionState === 'wave' && (
        <style>{`
          @keyframes tomBounce {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            25% { transform: translateY(-3px) rotate(-3deg); }
            50% { transform: translateY(0) rotate(0deg); }
            75% { transform: translateY(-2px) rotate(3deg); }
          }
          .tom-wave-bounce { animation: tomBounce 1.4s ease-in-out infinite; }
          @keyframes tomBubbleIn {
            0% { opacity: 0; transform: translateY(8px) scale(0.95); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          .tom-bubble-in { animation: tomBubbleIn 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards; transform-origin: bottom right; }
        `}</style>
      )}

      {!open && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
          {/* Sprechblase im Wave-Mode */}
          {attentionState === 'wave' && teaserShort && (
            <div
              className="tom-bubble-in relative bg-white text-[#1f1f1f] px-3 py-2.5 shadow-xl border border-[#befa0a] max-w-[240px] cursor-pointer hover:bg-[#fafafa] transition-colors"
              onClick={handleOpen}
              role="button"
              aria-label={t.openAria}
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <button
                onClick={dismissWave}
                aria-label="Hinweis schließen"
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs flex items-center justify-center rounded-full shadow"
              >
                ×
              </button>
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-0.5">
                  <img src="/assets/tom.jpg" alt="Tom" className="w-6 h-6 rounded-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#658a07] leading-none mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    Tom · KI-Assistent
                  </p>
                  <p className="text-xs leading-snug">{teaserShort}</p>
                </div>
              </div>
              {/* kleines Sprechblase-Schwänzchen */}
              <div className="absolute -bottom-1.5 right-5 w-3 h-3 bg-white border-r border-b border-[#befa0a] transform rotate-45" />
            </div>
          )}

          {/* Chat-Button (gleicher wie immer, optional mit Bounce-Animation) */}
          <button
            onClick={handleOpen}
            aria-label={t.openAria}
            className={`w-14 h-14 bg-[#befa0a] text-[#0a0a0a] flex items-center justify-center shadow-xl hover:bg-white transition-colors ${attentionState === 'wave' ? 'tom-wave-bounce ring-2 ring-[#befa0a]/40 ring-offset-2' : ''}`}
          >
            <MessageSquare size={24} />
          </button>
        </div>
      )}

      {open && (
        <div className="fixed inset-x-0 bottom-0 md:inset-x-auto md:bottom-6 md:right-6 z-50 flex flex-col bg-[#0a0a0a] border border-[#2a2a2a] shadow-2xl md:w-80 md:rounded-none w-full"
          style={{ height: '90vh', maxHeight: '600px' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a]">
            <div className="flex items-center gap-2.5">
              <img src="/assets/tom.jpg" alt="Tom" className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <div>
                <p className="text-[#befa0a] text-sm font-bold uppercase tracking-widest"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {t.headerTitle}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#befa0a]" />
                  <span className="text-white/40 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    EnBet Consulting
                  </span>
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Consent Screen */}
          {!consented ? (
            <div className="flex-1 flex flex-col justify-center px-6 py-8">
              {proactiveTeaser && (
                <div className="mb-5 px-3 py-3 bg-[#161616] border border-[#befa0a]/40 text-white text-sm leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {proactiveTeaser}
                </div>
              )}
              <p className="text-white text-sm leading-relaxed mb-6"
                style={{ fontFamily: "'Manrope', sans-serif" }}>
                {t.consentWelcome}<br /><br />
                {t.consentText}
                <button onClick={() => window.dispatchEvent(new Event('openDatenschutz'))}
                  className="text-[#befa0a] hover:underline font-bold underline">
                  {t.consentPrivacyLink}
                </button>{t.consentSuffix}
              </p>
              <button
                onClick={handleConsent}
                className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 text-sm font-bold uppercase tracking-widest hover:bg-white transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {t.consentButton}
              </button>
            </div>

          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                {messages.map((msg, i) => (
                  <div key={i} className="flex flex-col">
                    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[82%] px-3 py-2 text-sm leading-relaxed whitespace-pre-line ${
                          msg.role === 'user'
                            ? 'bg-[#befa0a] text-[#0a0a0a] font-medium'
                            : 'bg-[#161616] text-white/80 border border-[#252525]'
                        }`}
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {msg.content}
                      </div>
                    </div>
                    {msg.alternatives && (
                      <div className="flex flex-col gap-1 mt-1 ml-0">
                        {msg.alternatives.map((alt, ai) => (
                          <button key={ai} onClick={() => send(alt.label)} disabled={loading}
                            className="text-left text-xs px-3 py-1.5 border border-[#befa0a]/50 text-[#befa0a] hover:bg-[#befa0a] hover:text-[#0a0a0a] transition-colors disabled:opacity-40"
                            style={{ fontFamily: "'Manrope', sans-serif" }}>
                            {alt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-[#161616] border border-[#252525] px-3 py-2 text-white/30 text-sm"
                      style={{ fontFamily: "'Manrope', sans-serif" }}>
                      <span className="animate-pulse">···</span>
                    </div>
                  </div>
                )}

                {showCalendar && (
                  <div className="flex justify-start">
                    <button
                      onClick={() => { setOpen(false); setTimeout(() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }), 200); }}
                      className="inline-flex items-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    >
                      <Calendar size={13} />
                      {t.bookCalendar}
                    </button>
                  </div>
                )}

                {bookingConfirmed && (
                  <div className="mx-1 p-3 bg-[#befa0a] text-[#0a0a0a]">
                    <p className="text-xs font-bold uppercase tracking-wider mb-1"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {t.bookingConfirmedHeader}
                    </p>
                    <p className="text-xs leading-snug" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {t.bookingConfirmedText}
                      {bookingConfirmed.meet_link && (
                        <> &nbsp;<a href={bookingConfirmed.meet_link} target="_blank" rel="noopener noreferrer"
                          className="underline font-bold">{t.meetingLink}</a></>
                      )}
                    </p>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="px-3 py-3 border-t border-[#2a2a2a] flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={t.inputPlaceholder}
                  maxLength={1000}
                  className="flex-1 bg-transparent border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-[#befa0a] placeholder-white/20"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                />
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="bg-[#befa0a] text-[#0a0a0a] px-3 py-2 hover:bg-white transition-colors disabled:opacity-30 flex-shrink-0"
                >
                  <Send size={15} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
